import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  GripVertical,
  Settings,
  Trash2,
  Plus,
  X,
  Sparkles,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { GHOST_FIELD_CLASSES } from "../cells/GhostInput";
import {
  QuoteItemLine,
  Item,
  HydratedQuoteItemLine,
  CostingMethodEnum,
  QuantityBreak,
  ItemReviewStatus,
  isMultiCurrency,
  type CurrencyCode,
  formatPricingUnit,
  ITEM_UNIT_ABBREVIATION,
  type DimensionUnit,
  type ItemUnit,
  availableItemOverrideFields,
  isItemOverrideFieldEnabled,
} from "@forgepoint/shared";
import { getConversionTooltipText } from "@/modules/priceLists/pricingUnitUtils";
import { ItemSelector } from "@/components/ItemSelector";
import { useItemsStore } from "@/modules/items/itemsStore";
import { overridesAllowedForItem } from "./itemOverrides";
import { QuantityCell } from "../cells/QuantityCell";
import { LineUnitCell } from "../cells/LineUnitCell";
import { CurrencyCell } from "../cells/CurrencyCell";
import { LineTotalCell } from "../cells/LineTotalCell";
import { ConvertedUnitPriceTooltip } from "../cells/ConvertedUnitPriceTooltip";
import { LineMarginCell } from "../cells/LineMarginCell";
import {
  displayedUnitPrice,
  type QuoteCurrencyFields,
} from "@/lib/quoteCurrency";
import {
  calculateLineTotal,
  lineMargin,
  lineUnitWeight,
  pricePerWeightUnit,
} from "../../utils";
import { UnitPriceSubline } from "./UnitPriceSubline";
import { LineWeightSubline } from "./LineWeightSubline";
import { ConfidenceBadge } from "../ConfidenceBadge";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { useItemSettingsStore } from "@/store/itemSettingsStore";
import { ReviewStatusIndicator } from "../ReviewStatusIndicator";
import { LineCustomFieldsCollapsible } from "@/components/customFields/LineCustomFieldsCollapsible";
import type {
  CustomFieldValues,
  QuoteLineItemOverrides,
} from "@forgepoint/shared";
import { LineItemOverridesCollapsible } from "./LineItemOverridesCollapsible";

interface ItemParentRowProps {
  id: string;
  lineNumber: number | null;
  line: QuoteItemLine;
  selectedItem: Item | null;
  hydratedLine?: HydratedQuoteItemLine;
  readOnly: boolean;
  hasMultipleQuantities: boolean;
  reviewStatus?: ItemReviewStatus | null;
  currencyCode?: CurrencyCode;
  currencyContext?: QuoteCurrencyFields;
  currencyContextDirty?: boolean;
  onMarkReviewed?: () => void;
  onUpdate: (line: QuoteItemLine) => void;
  onRemove: () => void;
  onOpenBOM: () => void;
  onQuantityChange: (value: string) => void;
  onAddQuantity?: () => void;
  onSetOverride: (currentPrice: string) => void;
  onOverrideChange: (value: string) => void;
  onClearOverride: () => void;
}

/**
 * First row of an item line, showing add-quantity only when a handler is given.
 * @param props - The line, its item, display context, and change handlers
 * @returns The item line's parent row
 */
export function ItemParentRow({
  id,
  lineNumber,
  line,
  selectedItem,
  hydratedLine,
  readOnly,
  hasMultipleQuantities,
  reviewStatus,
  currencyCode,
  currencyContext,
  currencyContextDirty,
  onMarkReviewed,
  onUpdate,
  onRemove,
  onOpenBOM,
  onQuantityChange,
  onAddQuantity,
  onSetOverride,
  onOverrideChange,
  onClearOverride,
}: ItemParentRowProps) {
  const showConfidenceScore = useQuoteSettingsStore(
    (state) => state.settings?.showConfidenceScore ?? false,
  );
  const showTotalWeight = useQuoteSettingsStore(
    (state) => state.settings?.showTotalWeight ?? false,
  );
  const lineCustomFieldsSchema = useQuoteSettingsStore(
    (state) => state.settings?.lineItemCustomFieldsSchema ?? null,
  );
  const quoteSettings = useQuoteSettingsStore((state) => state.settings);
  const enableAllowance = useItemSettingsStore(
    (state) => state.settings?.enableAllowance ?? false,
  );
  const enabledOverrideFields = availableItemOverrideFields(
    quoteSettings,
    enableAllowance,
  );
  const dimensionUnitOverrideEnabled = isItemOverrideFieldEnabled(
    quoteSettings,
    "dimensionUnit",
  );
  const allowLineUnitOverride = useQuoteSettingsStore(
    (state) => state.settings?.allowLineUnitOverride ?? false,
  );
  const showMargin = useFeatureFlag("quote-line-margin");
  const getItem = useItemsStore((state) => state.getItem);

  const hasLineCustomFields =
    lineCustomFieldsSchema?.properties &&
    Object.keys(lineCustomFieldsSchema.properties).length > 0;

  const handleCustomFieldChange = (
    fieldName: string,
    value: CustomFieldValues[string],
  ) => {
    if (readOnly) {
      return;
    }
    onUpdate({
      ...line,
      customFields: { ...(line.customFields ?? {}), [fieldName]: value },
    });
  };

  const handleItemOverridesChange = (next: QuoteLineItemOverrides | null) => {
    if (readOnly) {
      return;
    }
    onUpdate({ ...line, itemOverrides: next });
  };

  const handleUnitChange = (next: ItemUnit | null) => {
    if (readOnly) {
      return;
    }
    onUpdate({ ...line, unitOverride: next });
  };

  const handleDimensionUnitChange = (next: DimensionUnit | null) => {
    if (readOnly) {
      return;
    }
    onUpdate({ ...line, dimensionUnitOverride: next });
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  /**
   * Switches the line's item, keeping the overrides the new item accepts.
   * @param newItemId - The selected item id, or null when cleared
   * @returns Nothing
   */
  const handleItemSelect = (newItemId: string | null) => {
    if (readOnly) {
      return;
    }
    const itemChanged = newItemId !== line.itemId;
    // Clear unitPriceOverride on all quantity breaks when the item changes,
    // since the override was set for the previous item's price
    const updatedBreaks = itemChanged
      ? line.quantityBreaks.map(({ unitPriceOverride: _, ...rest }) => rest)
      : line.quantityBreaks;
    const newItem = newItemId ? getItem(newItemId) : undefined;
    const updatedDescription =
      itemChanged && newItem
        ? (newItem.description ?? newItem.name)
        : line.description;
    onUpdate({
      ...line,
      itemId: newItemId,
      description: updatedDescription,
      confidenceScore: itemChanged ? null : line.confidenceScore,
      quantityBreaks: updatedBreaks,
      itemOverrides: itemChanged
        ? overridesAllowedForItem(line.itemOverrides, newItem)
        : line.itemOverrides,
    });
  };

  const handleDescriptionChange = (value: string) => {
    if (readOnly) {
      return;
    }
    onUpdate({ ...line, description: value });
  };

  // Check if hydrated data matches current item selection
  const isHydratedDataValid = hydratedLine?.itemId === line.itemId;
  // effectiveItem carries the line's item overrides; the catalog item does not.
  const effectiveItem =
    (isHydratedDataValid
      ? (hydratedLine?.effectiveItem ?? hydratedLine?.item)
      : null) ?? selectedItem;
  const lineUnit = line.unitOverride ?? selectedItem?.unit ?? null;
  const displayedUnit = allowLineUnitOverride ? lineUnit : selectedItem?.unit;

  const editLabel = reviewStatus ? "Review item" : "Edit item";

  // First quantity data
  const firstBreak: QuantityBreak | undefined = line.quantityBreaks[0];
  const hydratedFirstBreak = isHydratedDataValid
    ? hydratedLine?.quantityBreaks?.[0]
    : undefined;
  const hasOverride = firstBreak?.unitPriceOverride !== undefined;
  const overrideValue = hasOverride ? firstBreak?.unitPriceOverride : undefined;
  const needsPriceOverride =
    !!line.unitOverride &&
    hydratedFirstBreak?.unitPrice === null &&
    !hasOverride;

  const quantityChanged = firstBreak?.quantity !== hydratedFirstBreak?.quantity;
  const shouldShowSkeleton = !hasOverride && quantityChanged;

  const multiCurrency = !!currencyContext && isMultiCurrency(currencyContext);
  // The override is entered and stored in the quote's display currency.
  const overrideCurrencyCode = multiCurrency
    ? (currencyContext?.quoteCurrency ?? currencyCode)
    : currencyCode;
  const lineTotal = calculateLineTotal(
    overrideValue,
    firstBreak?.quantity,
    hydratedFirstBreak?.unitPrice,
    currencyContext,
  );
  // Prefer the backend-stamped display amount while the editor state still
  // matches the hydrated break; live edits fall back to shared-math derivation.
  // A dirty currency context means the stamped amount was computed under the
  // previous currency/snapshot, so it is stale and must be re-derived.
  const hydratedBreakFresh =
    !currencyContextDirty &&
    !quantityChanged &&
    (hydratedFirstBreak?.unitPriceOverride ?? null) === (overrideValue ?? null);
  const freshConvertedAmount = hydratedBreakFresh
    ? (hydratedFirstBreak?.convertedAmount ?? null)
    : null;

  const derivedUnitPrice = displayedUnitPrice(
    hydratedFirstBreak,
    currencyContext,
    !!currencyContextDirty,
  );

  const conversionTooltipText = getConversionTooltipText(
    hydratedFirstBreak,
    lineUnit,
  );

  const decimals = currencyContext?.quotePriceRounding?.decimals;
  const unitWeight = lineUnitWeight(
    hydratedFirstBreak,
    effectiveItem,
    lineUnit,
  );
  const perWeight = showTotalWeight
    ? pricePerWeightUnit(
        overrideValue ?? derivedUnitPrice,
        unitWeight,
        decimals,
      )
    : null;
  const perWeightSubline = perWeight ? (
    <UnitPriceSubline
      value={perWeight.value}
      unit={perWeight.unit}
      tooltip={`Unit price ÷ weight per unit (${unitWeight?.weight} ${formatPricingUnit(perWeight.unit)})`}
      currencyCode={overrideCurrencyCode}
      decimals={decimals}
    />
  ) : null;

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        className={cn("group border-b-0", isDragging && "opacity-50")}
      >
        {/* Drag Handle */}
        <TableCell className="pr-0 py-2 px-1 sm:px-2">
          {!readOnly && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing touch-manipulation p-1"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </TableCell>

        {/* Line Number */}
        <TableCell className="py-2 px-0 sm:px-1 text-center">
          {lineNumber !== null && (
            <span className="text-xs tabular-nums text-muted-foreground">
              {lineNumber}
            </span>
          )}
        </TableCell>

        {/* Item Selector */}
        <TableCell className="py-2 pb-1 px-1 sm:px-2 max-w-0">
          <div className="flex items-center gap-1.5">
            {reviewStatus && (
              <ReviewStatusIndicator
                status={reviewStatus}
                onClick={onMarkReviewed}
              />
            )}
            <ItemSelector
              currentlySelectedId={line.itemId}
              onSelectId={handleItemSelect}
              placeholder="Select item..."
              disabled={readOnly}
              variant="ghost"
              className="font-semibold text-xs sm:text-sm"
              enableQuickCreate={!readOnly}
              showTriggerCost={false}
              enableEdit={false}
            />
          </div>
        </TableCell>

        {/* Quantity Input - First quantity */}
        <TableCell className="text-right py-2 px-1 sm:px-2">
          <div className="flex flex-col items-end gap-0.5">
            <QuantityCell
              value={firstBreak?.quantity ?? ""}
              onChange={onQuantityChange}
              disabled={readOnly}
            />
            {showTotalWeight && (
              <LineWeightSubline
                weight={hydratedFirstBreak?.totalWeight}
                unit={
                  hydratedFirstBreak?.totalWeightUnit ??
                  effectiveItem?.weightUnit
                }
              />
            )}
          </div>
        </TableCell>

        {/* Unit */}
        <TableCell className="text-center py-2 px-1 sm:px-2">
          {allowLineUnitOverride && !readOnly && selectedItem ? (
            <LineUnitCell
              value={line.unitOverride}
              itemUnit={selectedItem.unit}
              onChange={handleUnitChange}
              needsPriceOverride={needsPriceOverride}
            />
          ) : (
            <span className="text-xs sm:text-sm text-muted-foreground">
              {displayedUnit ? ITEM_UNIT_ABBREVIATION[displayedUnit] : "-"}
            </span>
          )}
        </TableCell>

        {/* Margin - First quantity */}
        {showMargin && (
          <TableCell className="text-right py-2 px-1 sm:px-2">
            <LineMarginCell
              margin={lineMargin(
                overrideValue,
                hydratedFirstBreak?.unitPrice,
                hydratedFirstBreak?.unitCost,
                currencyContext,
              )}
              quantity={hydratedFirstBreak?.quantity}
              baseCurrencyCode={currencyCode}
              isBom={effectiveItem?.costingMethod === CostingMethodEnum.BOM}
              shouldShowSkeleton={shouldShowSkeleton}
            />
          </TableCell>
        )}

        {/* Unit Price (with override if present) - First quantity */}
        <TableCell className="text-right py-2 px-0.5 sm:px-2">
          <div className="flex flex-col items-end gap-0.5">
            {hasOverride ? (
              <>
                <div className="flex w-full items-center justify-end gap-0.5">
                  <CurrencyCell
                    value={overrideValue ?? null}
                    onChange={(v) => onOverrideChange(v)}
                    disabled={readOnly}
                    editable={!readOnly}
                    isUnitPrice
                    currencyCode={overrideCurrencyCode}
                    decimals={decimals}
                  />
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 shrink-0 sm:h-7 sm:w-7"
                      onClick={() => onClearOverride()}
                    >
                      <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    </Button>
                  )}
                </div>
                {hydratedFirstBreak?.unitPrice && (
                  <ConvertedUnitPriceTooltip
                    baseUnitPrice={hydratedFirstBreak.unitPrice}
                    baseCurrencyCode={currencyCode}
                    currencyContext={currencyContext}
                    className="text-[10px] sm:text-xs tabular-nums line-through text-muted-foreground"
                  />
                )}
                {perWeightSubline}
              </>
            ) : shouldShowSkeleton ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex justify-end">
                      <Skeleton className="h-4 w-10 sm:h-5 sm:w-16" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Save your updates to recalculate prices</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : hydratedFirstBreak?.unitPrice ? (
              <div className="flex w-full items-center justify-end gap-0.5">
                <div className="flex flex-col items-end">
                  {readOnly ? (
                    <ConvertedUnitPriceTooltip
                      baseUnitPrice={hydratedFirstBreak.unitPrice}
                      baseCurrencyCode={currencyCode}
                      currencyContext={currencyContext}
                      className="text-xs sm:text-sm tabular-nums"
                    />
                  ) : (
                    <button
                      type="button"
                      aria-label="Override price"
                      onClick={() => onSetOverride(derivedUnitPrice ?? "")}
                      className={cn(
                        "-mx-0.5 sm:-mx-2 flex h-6 sm:h-8 items-center rounded-md px-0.5 sm:px-2 py-0.5 sm:py-1 transition-colors",
                        GHOST_FIELD_CLASSES,
                      )}
                    >
                      <ConvertedUnitPriceTooltip
                        baseUnitPrice={hydratedFirstBreak.unitPrice}
                        baseCurrencyCode={currencyCode}
                        currencyContext={currencyContext}
                        className="text-xs sm:text-sm tabular-nums"
                      />
                    </button>
                  )}
                  {hydratedFirstBreak.convertedFromUnitPrice &&
                  hydratedFirstBreak.pricingUnit ? (
                    <UnitPriceSubline
                      value={hydratedFirstBreak.convertedFromUnitPrice}
                      unit={hydratedFirstBreak.pricingUnit}
                      tooltip={conversionTooltipText}
                      currencyCode={currencyCode}
                      decimals={decimals}
                    />
                  ) : (
                    perWeightSubline
                  )}
                </div>
              </div>
            ) : (
              <span className="text-xs sm:text-sm text-muted-foreground">
                -
              </span>
            )}
          </div>
        </TableCell>

        {/* Line Total - First quantity */}
        <TableCell className="text-right py-2 px-1 sm:px-2">
          <LineTotalCell
            lineTotal={lineTotal}
            unitPrice={hydratedFirstBreak?.unitPrice ?? null}
            quantity={firstBreak?.quantity ?? null}
            overrideUnitPrice={overrideValue ?? null}
            convertedAmount={freshConvertedAmount}
            shouldShowSkeleton={shouldShowSkeleton}
            currencyCode={currencyCode}
            currencyContext={currencyContext}
          />
        </TableCell>

        {/* Actions */}
        <TableCell className="py-2 px-1 sm:px-2">
          <div className="flex items-center gap-0.5 sm:gap-1">
            {selectedItem && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={editLabel}
                      className={cn(
                        "group/edit h-7 sm:h-8 px-2 sm:px-2.5 gap-1.5 rounded-full border active:scale-95 touch-manipulation transition-[color,background-color,border-color,box-shadow,transform] duration-200 motion-reduce:transition-none",
                        reviewStatus === "NEEDS_REVIEW"
                          ? "border-amber-400/60 bg-amber-400/10 text-amber-600 shadow-[0_0_10px_rgba(245,158,11,0.45)] hover:bg-amber-400/20 hover:text-amber-700 hover:border-amber-400"
                          : "border-transparent text-muted-foreground hover:text-primary hover:bg-primary/10 hover:border-primary/20",
                      )}
                      onClick={onOpenBOM}
                    >
                      <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform duration-300 ease-out group-hover/edit:rotate-90 motion-reduce:transition-none motion-reduce:group-hover/edit:rotate-0" />
                      <span className="hidden @[768px]/quote-items:inline">
                        {editLabel}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{editLabel}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {!hasMultipleQuantities && !readOnly && onAddQuantity && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 sm:h-8 sm:w-8 touch-manipulation"
                      onClick={onAddQuantity}
                    >
                      <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Add another quantity option</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {!readOnly && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 sm:h-8 sm:w-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity touch-manipulation"
                onClick={onRemove}
              >
                <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
      <TableRow
        className={cn(
          hasMultipleQuantities ? "border-b-0" : "border-b border-border",
          isDragging && "opacity-50",
        )}
      >
        <TableCell />
        <TableCell />
        <TableCell
          colSpan={showMargin ? 7 : 6}
          className="pt-0 pb-2 px-1 sm:px-2"
        >
          <div className="flex flex-col gap-0.5">
            <Textarea
              value={line.description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder="Description"
              disabled={readOnly}
              minRows={1}
              className={cn(
                "min-h-8 px-2 py-[5px] shadow-none text-xs sm:text-sm text-muted-foreground",
                GHOST_FIELD_CLASSES,
              )}
            />
            {((showConfidenceScore &&
              line.confidenceScore !== null &&
              line.confidenceScore !== undefined) ||
              line.reasoning) && (
              <div className="mx-2 mt-0.5 flex items-start gap-1.5 rounded border border-dashed border-muted-foreground/25 bg-muted/40 px-2 py-1">
                <div className="flex h-4 shrink-0 items-center gap-1.5">
                  <Sparkles className="h-3 w-3 shrink-0 text-muted-foreground/60" />
                  {showConfidenceScore &&
                    line.confidenceScore !== null &&
                    line.confidenceScore !== undefined && (
                      <ConfidenceBadge score={line.confidenceScore} />
                    )}
                </div>
                {line.reasoning && (
                  <span className="whitespace-pre-line text-xs italic text-muted-foreground/80">
                    {line.reasoning}
                  </span>
                )}
              </div>
            )}
            {hasLineCustomFields && lineCustomFieldsSchema && (
              <LineCustomFieldsCollapsible
                lineId={id}
                schema={lineCustomFieldsSchema}
                values={line.customFields ?? {}}
                readOnly={readOnly}
                onChange={handleCustomFieldChange}
              />
            )}
            <LineItemOverridesCollapsible
              value={line.itemOverrides}
              onChange={handleItemOverridesChange}
              dimensionUnit={line.dimensionUnitOverride}
              onDimensionUnitChange={handleDimensionUnitChange}
              catalogItem={selectedItem}
              effectiveItem={effectiveItem}
              enabledFields={enabledOverrideFields}
              dimensionUnitEnabled={dimensionUnitOverrideEnabled}
              readOnly={readOnly}
            />
          </div>
        </TableCell>
      </TableRow>
    </>
  );
}
